import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';
import { articleData, allArticleSlugs } from '@/mocks/articleContent';
import type { ArticleContent } from '@/mocks/articleContent';
import { useState } from 'react';
import { SITE_URL } from '@/constants';

function buildArticleSchema(article: ArticleContent) {
  const articleUrl = SITE_URL + '/article/' + article.slug;
  const schemas: Record<string, unknown>[] = [];

  schemas.push({
    '@type': 'Article',
    headline: article.title,
    description: article.title + '. Read this comprehensive guide on the Superbuy Spreadsheet Hub.',
    url: articleUrl,
    datePublished: '2026-07-13',
    dateModified: '2026-07-13',
    author: {
      '@type': 'Organization',
      name: 'Superbuy Spreadsheet',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Superbuy Spreadsheet',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04004e8ea78b88107.png',
      },
    },
    image: article.heroImage,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    isPartOf: {
      '@type': 'WebSite',
      name: 'Superbuy Spreadsheet',
      url: SITE_URL,
    },
  });

  if (article.faqs && article.faqs.length > 0) {
    schemas.push({
      '@type': 'FAQPage',
      mainEntity: article.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': schemas,
  };
}

function ArticleNotFound() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-24 md:pt-28 pb-16 md:pb-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-accent-100 rounded-full">
            <i className="ri-file-search-line text-accent-600 text-3xl"></i>
          </div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
            Article Not Found
          </h1>
          <p className="text-foreground-500 text-sm md:text-base mb-8">
            The guide you are looking for does not exist or may have been moved.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            Back to Superbuy Spreadsheet Hub
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ArticleRenderer({ article }: { article: ArticleContent }) {
  const [openFaqs, setOpenFaqs] = useState<Set<number>>(new Set());
  const schema = buildArticleSchema(article);

  const toggleFaq = (index: number) => {
    setOpenFaqs((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <JsonLd data={schema} />
      <main>
        <article className="pt-24 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-foreground-500 hover:text-primary-500 text-sm font-medium transition-colors duration-200 mb-6"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Superbuy Spreadsheet Hub
            </Link>

            <div className="mb-2">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full">
                {article.tag}
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-foreground-400 text-xs md:text-sm mb-8 pb-8 border-b border-background-200">
              <span>Updated: July 2026</span>
              <span>·</span>
              <span>{article.readTime}</span>
              <span>·</span>
              <span>Independent Educational Resource</span>
            </div>

            <div className="relative aspect-[16/9] rounded-2xl overflow-hidden mb-10">
              <img
                src={article.heroImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose prose-lg max-w-none">
              {article.sections.map((section, idx) => {
                switch (section.type) {
                  case 'paragraph':
                    return (
                      <p key={idx} className="text-foreground-700 text-base md:text-lg leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: section.content || '' }} />
                    );

                  case 'heading':
                    return (
                      <h2 key={idx} className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
                        {section.heading}
                      </h2>
                    );

                  case 'subheading':
                    return (
                      <h3 key={idx} className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mt-8 mb-3">
                        {section.subheading}
                      </h3>
                    );

                  case 'highlight':
                    return (
                      <div key={idx} className="bg-background-100 rounded-2xl p-6 md:p-8 my-8">
                        <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4 flex items-center gap-2">
                          <i className="ri-lightbulb-line text-accent-500"></i>
                          {section.highlightTitle}
                        </h3>
                        <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                          {section.highlightText}
                        </p>
                      </div>
                    );

                  case 'steps':
                    return (
                      <div key={idx} className="space-y-5 my-8">
                        {(section.steps || []).map((item) => (
                          <div key={item.step} className="flex gap-4 p-5 bg-background-100 rounded-xl">
                            <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-primary-500 text-background-50 rounded-xl font-heading font-bold text-lg">
                              {item.step}
                            </div>
                            <div>
                              <h4 className="text-foreground-900 font-heading font-semibold text-base md:text-lg mb-1.5">
                                {item.title}
                              </h4>
                              <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
                                {item.desc}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    );

                  case 'table':
                    return (
                      <div key={idx} className="overflow-x-auto my-8">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="border-b-2 border-background-200">
                              {(section.table?.headers || []).map((header, hi) => (
                                <th key={hi} className="text-left py-3 px-4 text-foreground-900 font-semibold text-sm whitespace-nowrap">
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(section.table?.rows || []).map((row, ri) => (
                              <tr key={ri} className="border-b border-background-200/70">
                                {row.map((cell, ci) => (
                                  <td key={ci} className="py-3 px-4 text-foreground-600 text-sm leading-relaxed">
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );

                  case 'cta':
                    return (
                      <div key={idx} className="bg-primary-50 border border-primary-200/50 rounded-2xl p-6 md:p-8 mt-10 mb-8">
                        <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">
                          {section.ctaText}
                        </h3>
                        <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5">
                          Now that you have a solid understanding, the next step is to dive into the actual resources. Visit the main platform to browse categories and put your new knowledge into practice.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <a
                            href="https://kaspreadsheet.com"
                            target="_blank"
                            rel="noopener nofollow"
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
                          >
                            <i className="ri-shopping-bag-3-line"></i>
                            Superbuy Sheet
                          </a>
                          <Link
                            to="/"
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-100 text-foreground-900 rounded-full text-sm font-semibold hover:bg-background-200 transition-all duration-200 whitespace-nowrap"
                          >
                            <i className="ri-home-4-line"></i>
                            Back to Homepage
                          </Link>
                        </div>
                      </div>
                    );

                  default:
                    return null;
                }
              })}

              {article.faqs.length > 0 && (
                <>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
                    Frequently Asked Questions
                  </h2>

                  <div className="space-y-4 my-8">
                    {article.faqs.map((faq, i) => (
                      <div key={i} className="bg-background-100 rounded-xl overflow-hidden">
                        <button
                          onClick={() => toggleFaq(i)}
                          className="flex items-center justify-between w-full p-5 text-left cursor-pointer"
                        >
                          <span className={`font-semibold text-sm md:text-base transition-colors ${openFaqs.has(i) ? 'text-primary-500' : 'text-foreground-900'}`}>
                            {faq.q}
                          </span>
                          <span className="flex-shrink-0 ml-3">
                            {openFaqs.has(i) ? (
                              <i className="ri-subtract-line text-foreground-400 text-lg"></i>
                            ) : (
                              <i className="ri-add-line text-foreground-400 text-lg"></i>
                            )}
                          </span>
                        </button>
                        {openFaqs.has(i) && (
                          <div className="px-5 pb-5">
                            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                              {faq.a}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </article>

        <section className="w-full py-12 md:py-16 px-4 md:px-6 lg:px-10 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
              Explore More Resources
            </h3>
            <p className="text-foreground-500 text-sm mb-6">
              Check out our other guides and resources to continue learning about Superbuy Spreadsheet.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
            >
              Browse All Guides
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug || !allArticleSlugs.includes(slug)) {
    return <ArticleNotFound />;
  }

  const article = articleData[slug];

  return <ArticleRenderer article={article} />;
}